import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, FolderSync, Layers, Edit2, X } from "lucide-react";
import { ICategory } from "../../../types/Response/AdminResponse";
import { useAddSubcategoryMutation } from "../../../redux/actions/Admin/dashboardAdmin";
import CommonButton from "../../../components/CommonButton";
import TextInputComp from "../../../components/customer/TextInputComp";

interface ISubcategory {
    id: number;
    name: string;
    imageUrl?: string;
}

const AddNewSubCategory: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const [addSubcategory, { isLoading }] = useAddSubcategoryMutation();

    // Grab the incoming navigation state
    const { selectedSection, selectedCategory } = (location.state as {
        selectedSection: string;
        selectedCategory: ICategory & { subcategories?: ISubcategory[] };
    }) || { selectedSection: "", selectedCategory: null };

    const [isAdding, setIsAdding] = useState<boolean>(false);
    const [enabledStatus, setEnabledStatus] = useState<Record<number, boolean>>({});

    const [name, setName] = useState("");
    const [imageUrl, setImageUrl] = useState("");
    const [preview, setPreview] = useState("");

    const [feedback, setFeedback] = useState<{ type: "success" | "error"; text: string } | null>(null);

    // Dynamic safe check for parentId extraction
    const targetParentId = selectedCategory?.id || (typeof selectedCategory === "number" || typeof selectedCategory === "string" ? Number(selectedCategory) : null);

    const handleToggleStatus = (id: number) => {
        setEnabledStatus((prev) => ({
            ...prev,
            [id]: prev[id] === false ? true : false,
        }));
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setFeedback(null);

        if (!targetParentId) {
            setFeedback({ type: "error", text: "Missing parent category context reference identity." });
            return;
        }

        try {
            const requestPayload = {
                name: name.trim(),
                parentId: targetParentId,
                imageUrl: imageUrl,
            };

            const response = await addSubcategory(requestPayload).unwrap();

            if (response.success) {
                setName("");
                setImageUrl("");
                setPreview("");
                setIsAdding(false); // Cleanly close modal workspace overlay frame on completion
                setFeedback({ type: "success", text: "Sub-category created successfully!" });
            }
        } catch (err: any) {
            console.error("Caught RTK Error payload directly:", err);

            // Safe checking chain to isolate backend message vs custom text string
            const errorText = err?.data?.message || err?.message || "Failed to save sub-category. Please try again.";

            setFeedback({
                type: "error",
                text: errorText
            });
        }
    };

    const subcategoriesList = selectedCategory?.subcategories || [];

    return (
        <div className="px-2 mx-auto pb-10 max-w-7xl relative">
            {/* Navigation Row */}
            <div className="flex items-center justify-between mb-4">
                <button
                    onClick={() => navigate(-1)}
                    disabled={isLoading}
                    className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors text-slate-600 focus:outline-none disabled:opacity-50"
                >
                    <ArrowLeft className="w-4 h-4" />
                </button>
            </div>

            {/* Clean Segment Heading Block */}
            <div className="mb-6">
                <h1 className="text-2xl font-black tracking-tight" style={{ color: "#2F5E2E" }}>
                    Sub-Categories Management
                </h1>
                <p className="text-slate-500 text-xs mt-0.5">
                    Quickly view, add, or manage active subcatalog configurations.
                </p>
            </div>

            {/* Breadcrumbs Tracking Block */}
            <div className="mb-6 p-4 rounded-xl border border-slate-200 bg-slate-50/60 grid grid-cols-2 gap-4">
                <div>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">
                        Selected Section
                    </span>
                    <div className="flex items-center gap-2 text-sm font-bold text-slate-800 capitalize">
                        <Layers className="w-4 h-4" style={{ color: "#8B6B3E" }} />
                        {selectedSection || "None Selected"}
                    </div>
                </div>

                <div className="border-l border-slate-200 pl-4">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">
                        Parent Category (parentId: {targetParentId || "N/A"})
                    </span>
                    <div className="flex items-center gap-2 text-sm font-bold truncate" style={{ color: "#2F5E2E" }}>
                        <FolderSync className="w-4 h-4" style={{ color: "#6DBE45" }} />
                        {selectedCategory?.name || "None Selected"}
                    </div>
                </div>
            </div>

            {/* Runtime Feedback Alerts */}
            {feedback && (
                <div className={`mb-4 p-3 rounded-lg text-xs font-semibold border ${feedback.type === "success" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-red-50 text-red-800 border-red-200"}`}>
                    {feedback.text}
                </div>
            )}

            {selectedCategory ? (
                <div className="space-y-6">

                    {/* EXISTING LIST BOX */}
                    <div className="p-4 rounded-xl border border-slate-200 bg-white shadow-xs">
                        <div className="flex items-center justify-between gap-3 mb-6 pb-3 border-b border-slate-100">
                            <div className="flex flex-col">
                                <h3 className="text-logoHeading text-lg font-bold">Sub Categories</h3>
                                <span className="text-slate-400 text-xs mt-0.5">
                                    Configuring nodes under <strong className="text-slate-600 font-semibold">{selectedCategory?.name || "Selected Category"}</strong>
                                </span>
                            </div>
                            <CommonButton
                                text="Add Sub-Category"
                                icon={<Plus size={16} />}
                                variant="filled"
                                iconPosition="left"
                                rounded="md"
                                onClick={() => setIsAdding(true)}
                            />
                        </div>

                        {subcategoriesList.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
                                {subcategoriesList.map((subcategory: ISubcategory) => {
                                    const isCurrentEnabled = enabledStatus[subcategory.id] !== false;

                                    return (
                                        <div
                                            key={subcategory.id}
                                            className="group relative border border-slate-200 hover:border-slate-300 rounded-xl p-3.5 transition-all duration-150 flex flex-col justify-between bg-slate-50/40 hover:bg-white min-w-0"
                                        >
                                            <div className="flex items-center justify-between gap-2 w-full">
                                                <h3
                                                    style={{ color: isCurrentEnabled ? "#2F5E2E" : "#94A3B8" }}
                                                    className="text-base font-bold truncate transition-colors"
                                                >
                                                    {subcategory.name}
                                                </h3>
                                            </div>

                                            <div className="flex items-center pt-4 w-full justify-between">
                                                <button
                                                    type="button"
                                                    onClick={() => console.log("Edit identification target:", subcategory.id)}
                                                    className={`inline-flex items-center gap-1 px-2 py-1.5 text-xs font-medium rounded-md border transition-colors focus:outline-none ${isCurrentEnabled
                                                        ? "border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100"
                                                        : "border-slate-300 text-slate-400 bg-slate-100 cursor-not-allowed"
                                                        }`}
                                                >
                                                    <Edit2 className="w-3 h-3" />
                                                    Edit
                                                </button>

                                                <div className="flex items-center gap-1.5">
                                                    <span className="text-sm font-medium text-logoHeading select-none">
                                                        {isCurrentEnabled ? "Active" : "Off"}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleToggleStatus(subcategory.id)}
                                                        className="relative inline-flex h-4 w-8 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none"
                                                        style={{ backgroundColor: isCurrentEnabled ? "#5FB63F" : "#CBD5E1" }}
                                                    >
                                                        <span
                                                            className="pointer-events-none inline-block h-3 w-3 transform rounded-full bg-white shadow-xs ring-0 transition duration-200 ease-in-out"
                                                            style={{ transform: isCurrentEnabled ? "translateX(16px)" : "translateX(0px)" }}
                                                        />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="py-12 text-center text-xs text-slate-400 italic">
                                Nothing added yet. Click on "Add Sub-Category" above to populate this workspace section.
                            </div>
                        )}
                    </div>

                    {/* MODAL OVERLAY WRAPPER */}
                    {isAdding && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-xs p-4">
                            <div className="bg-white rounded-xl border border-slate-200 w-full max-w-md shadow-xl flex flex-col max-h-[90vh]">
                                {/* Modal Header */}
                                <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                                    <div>
                                        <h3 className="text-base font-bold text-slate-800">Add New Sub-Category</h3>
                                        <p className="text-[11px] text-slate-400 mt-0.5">Creating node inside {selectedCategory?.name || "Selected Category"}</p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setIsAdding(false)}
                                        className="p-1 rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
                                    >
                                        <X size={18} />
                                    </button>
                                </div>

                                {/* Modal Body */}
                                <form onSubmit={handleSave} className="p-5 space-y-5 overflow-y-auto">
                                    <div className="space-y-4">
                                        <div>
                                            <TextInputComp
                                                disabled={isLoading}
                                                value={name}
                                                onChangeText={setName}
                                                placeholder="e.g., Cat Dry Food, Premium Collars..."
                                                showLabel
                                                showStar
                                                label="Sub-Category Name"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-semibold text-slate-700 mb-1.5 tracking-wide">
                                                Category Image
                                            </label>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                disabled={isLoading}
                                                className="w-full text-xs text-slate-500 file:mr-4 file:py-2 file:px-3 file:rounded-md file:border file:border-slate-200 file:text-xs file:font-semibold file:bg-white file:text-slate-700 hover:file:bg-slate-50 cursor-pointer"
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0];
                                                    if (file) {
                                                        const localUrl = URL.createObjectURL(file);
                                                        setImageUrl(localUrl);
                                                        setPreview(localUrl);
                                                    }
                                                }}
                                            />
                                        </div>

                                        {preview && (
                                            <div className="border border-slate-200 rounded-lg p-3 flex justify-center bg-slate-50 mx-auto max-w-xs">
                                                <img
                                                    src={preview}
                                                    alt="Preview"
                                                    className="h-24 object-contain rounded-md"
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {/* Modal Actions */}
                                    <div className="pt-4 flex justify-end gap-3 border-t border-slate-100">
                                        <CommonButton
                                            text="Cancel"
                                            variant="outline"
                                            rounded="md"
                                            onClick={() => setIsAdding(false)}
                                        />
                                        <CommonButton
                                            text={isLoading ? "Saving..." : "Save Sub-Category"}
                                            icon={<Plus size={16} />}
                                            variant="filled"
                                            iconPosition="left"
                                            rounded="md"
                                            disabled={isLoading}
                                            className="bg-mainBrandColor text-white font-bold disabled:opacity-50"
                                        />
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div className="p-8 text-center border border-dashed border-red-200 bg-red-50/30 rounded-xl text-xs font-semibold text-red-600">
                    Routing mismatch tracking context error. Please back out and select a valid parent node element first.
                </div>
            )}
        </div>
    );
};

export default AddNewSubCategory;